import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { createIncome, updateIncome } from '../services/api';
import { trackAction } from '../services/analytics';
import toast from 'react-hot-toast';
import CurrencyInput from './CurrencyInput';
import useIsMobile from '../hooks/useIsMobile';

const INCOME_CATEGORIES = ['Loan', 'CapitalInjection', 'Refund', 'Interest', 'AssetSale', 'Grant', 'Other'];
const PAYMENT_METHODS = ['Cash', 'Transfer', 'Card', 'Nequi', 'Daviplata', 'Other'];

const getLocalISODate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const initialState = () => ({
    category: '',
    amount: '',
    date: getLocalISODate(),
    description: '',
    payment_method: 'Cash',
});

/**
 * CreateIncomeModal — crear/editar un "otro ingreso" (dinero que entra y NO es una venta).
 * Espejo simplificado de CreateExpenseModal (sin proveedor / deducible / liquidación).
 */
const CreateIncomeModal = ({ show, onHide, incomeToEdit = null, onIncomeCreated, onIncomeUpdated }) => {
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const [data, setData] = useState(initialState());

    useEffect(() => {
        if (incomeToEdit) {
            const formattedDate = incomeToEdit.date
                ? new Date(incomeToEdit.date).toISOString().split('T')[0]
                : getLocalISODate();
            setData({
                category: incomeToEdit.category || '',
                amount: incomeToEdit.amount ?? '',
                date: formattedDate,
                description: incomeToEdit.description || '',
                payment_method: incomeToEdit.payment_method || 'Cash',
            });
        } else {
            setData(initialState());
        }
    }, [incomeToEdit, show]);

    const handleChange = (e) => setData({ ...data, [e.target.name]: e.target.value });

    const handleClose = () => {
        setData(initialState());
        onHide();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const isEditing = !!incomeToEdit;
        const toastId = toast.loading(isEditing ? t('incomesPage.toasts.updating') : t('incomesPage.toasts.creating'));
        try {
            if (isEditing) {
                await updateIncome(incomeToEdit.income_id, data);
                trackAction('income_updated');
                toast.success(t('incomesPage.toasts.updated'), { id: toastId });
                if (onIncomeUpdated) onIncomeUpdated();
            } else {
                await createIncome(data);
                trackAction('income_created');
                toast.success(t('incomesPage.toasts.created'), { id: toastId });
                if (onIncomeCreated) onIncomeCreated();
            }
            setData(initialState());
            onHide();
        } catch (error) {
            trackAction('income_save_failed', { is_edit: isEditing });
            toast.error(isEditing ? t('incomesPage.toasts.updateFailed') : t('incomesPage.toasts.createFailed'), { id: toastId });
        }
    };

    return (
        <Modal show={show} onHide={handleClose} backdrop={isMobile ? 'static' : true}>
            <Modal.Header closeButton>
                <Modal.Title>{incomeToEdit ? t('incomesPage.modal.editTitle') : t('incomesPage.modal.createTitle')}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <p className="text-muted small">{t('incomesPage.modal.disclaimer')}</p>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3" controlId="incomeDate">
                                <Form.Label>{t('incomesPage.modal.dateLabel')}</Form.Label>
                                <Form.Control type="date" name="date" value={data.date} onChange={handleChange} required />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3" controlId="incomeCategory">
                                <Form.Label>{t('incomesPage.modal.categoryLabel')}</Form.Label>
                                <Form.Select name="category" value={data.category} onChange={handleChange} required>
                                    <option value="">{t('incomesPage.modal.categoryPlaceholder')}</option>
                                    {INCOME_CATEGORIES.map((c) => (
                                        <option key={c} value={c}>{t(`incomesPage.categories.${c}`)}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3" controlId="incomePaymentMethod">
                                <Form.Label>{t('incomesPage.modal.paymentMethodLabel')}</Form.Label>
                                <Form.Select name="payment_method" value={data.payment_method} onChange={handleChange}>
                                    {PAYMENT_METHODS.map((m) => (
                                        <option key={m} value={m}>{t(`incomesPage.paymentMethods.${m}`)}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3" controlId="incomeAmount">
                                <Form.Label>{t('incomesPage.modal.amountLabel')}</Form.Label>
                                <CurrencyInput
                                    name="amount"
                                    value={data.amount}
                                    onChange={(value) => setData({ ...data, amount: value })}
                                    required
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Group className="mb-3" controlId="incomeDescription">
                        <Form.Label>{t('incomesPage.modal.descriptionLabel')}</Form.Label>
                        <Form.Control as="textarea" rows={3} name="description" value={data.description} onChange={handleChange} />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>{t('cancel')}</Button>
                    <Button variant="primary" type="submit">
                        {t('incomesPage.modal.saveButton')}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default CreateIncomeModal;
